import {
  blue,
  teal,
  red,
  orange,
  gray,
  aliceblue,
  aliases,
  semanticNames,
  green,
  magenta,
  purple,
  aqua,
  tangerine,
  brown,
  warmgray,
  UI_THEMES,
  type UITheme,
  type SemanticColor,
  brand,
  deepteal,
  sage,
  lagoon,
  petrol,
  indigo,
  azure,
  clay,
  yolk,
  lightmist,
  neutral,
} from '../theme';
import type { ColorFamily, Palette, PaletteColorName } from './types';
export {
  UI_THEMES,
  semanticNames,
  type UITheme,
  type SemanticColorName,
  type SemanticColor,
} from '../theme';

type TealColor = keyof typeof teal;
type BlueColor = keyof typeof blue;
type RedColor = keyof typeof red;
type OrangeColor = keyof typeof orange;
type GrayColor = keyof typeof gray;
type AliceBlueColor = keyof typeof aliceblue;
type GreenColor = keyof typeof green;
type MagentaColor = keyof typeof magenta;
type PurpleColor = keyof typeof purple;
type AquaColor = keyof typeof aqua;
type TangerineColor = keyof typeof tangerine;
type BrownColor = keyof typeof brown;
type WarmGrayColor = keyof typeof warmgray;
type BrandColor = keyof typeof brand;
type DeeptealColor = keyof typeof deepteal;
type SageColor = keyof typeof sage;
type LagoonColor = keyof typeof lagoon;
type PetrolColor = keyof typeof petrol;
type IndigoColor = keyof typeof indigo;
type AzureColor = keyof typeof azure;
type ClayColor = keyof typeof clay;
type YolkColor = keyof typeof yolk;
type LightmistColor = keyof typeof lightmist;
type NeutralColor = keyof typeof neutral;

export type PaletteColor =
  | TealColor
  | BlueColor
  | RedColor
  | OrangeColor
  | GrayColor
  | AliceBlueColor
  | GreenColor
  | MagentaColor
  | PurpleColor
  | AquaColor
  | TangerineColor
  | BrownColor
  | WarmGrayColor
  | BrandColor
  | DeeptealColor
  | SageColor
  | LagoonColor
  | PetrolColor
  | IndigoColor
  | AzureColor
  | ClayColor
  | YolkColor
  | LightmistColor
  | NeutralColor;

export type Color = PaletteColor | SemanticColor;

export const buttonColors = [
  'primary',
  'secondary',
  'warning',
  'inherit',
  'tertiary',
] as const;

export type ButtonColor = (typeof buttonColors)[number] | 'white';

export const notificationColors = [
  'information',
  'success',
  'warning',
  'error',
] as const;

export type NotificationColor = (typeof notificationColors)[number];

export const colors: Record<ColorFamily, Palette> = {
  gray,
  aliceblue,
  teal,
  blue,
  red,
  orange,
  magenta,
  green,
  purple,
  aqua,
  tangerine,
  brown,
  warmgray,
  brand,
  deepteal,
  sage,
  lagoon,
  petrol,
  indigo,
  azure,
  clay,
  yolk,
  lightmist,
  neutral,
  aliases,
};

export const colorOptions = Object.keys(colors).reduce((acc: string[], key) => {
  return [...acc, ...Object.keys((colors as any)[key])];
}, []);

type SemanticThemeColors = Record<
  UITheme,
  Record<
    string,
    { colors: Record<string, { hex: string; color: PaletteColorName }> }
  >
>;

export const semantics = Object.keys(semanticNames).reduce<SemanticThemeColors>(
  (acc, name) => {
    const themes = Object.values(UI_THEMES) as UITheme[];
    themes.forEach((theme) => {
      const color = semanticNames[name as SemanticColor][theme];
      const splits = name.split('-');
      const colorSplits = color.split('-');
      const colorFamily = colorSplits[0] as ColorFamily;
      let palette = splits[0];
      let colorName = splits[1];

      if (splits.length === 3 && splits[0] !== 'border') {
        // Borders have a color called medium-light that needs special treatment
        palette = `${palette}-${splits[1]}`;
        colorName = splits[2];
      } else if (splits.length === 3) {
        palette = splits[0];
        colorName = `${splits[1]}-${splits[2]}`;
      } else if (splits[0] === 'viz') {
        // Vizualization colors have a color and might have a sequence that needs special treatment
        if (splits.length === 4) {
          palette = `${palette}-${splits[1]}`;
          colorName = `${splits[2]}-${splits[3]}`;
        } else {
          palette = `${palette}`;
          colorName = splits[1];
        }
      } else if (name.startsWith('agentic-workflow')) {
        palette = `${palette}-${splits[1]}`;
        colorName = `${splits[2]}-${splits[3]}`;
      }

      if (!acc[theme][palette]) {
        acc[theme][palette] = {
          colors: {},
        };
      }

      acc[theme][palette].colors[colorName] = {
        hex: colors[colorFamily][color],
        color,
      };
    });

    return acc;
  },
  {
    [UI_THEMES.LIGHT]: {},
    [UI_THEMES.HIGH_CONTRAST]: {},
    [UI_THEMES.QA]: {},
    [UI_THEMES.BRAND]: {},
  }
);
